import { Box, Button, TextField, Typography } from '@mui/material';
import { PropsWithChildren, useMemo, useRef } from 'react';
import { Document } from '../Document/Document';
import { UploadDocument } from '../UploadDocument';
import { Plus } from 'phosphor-react';
import UploadCloud from '../../assets/Icons/upload-cloud';
import { useModal } from '../../context/ModalProvider/useModal';
import FileAttachmentIcon from '../../assets/Icons/file-attachment-icon';
import { useAppSelector } from '../../store/hooks';
import axios from 'axios';
import TrashIcon from '../../assets/Icons/trash';
import VisibilityIcon from '../../assets/Icons/visibility-icon';
import { Info } from '@mui/icons-material';
import { ContentCard, Dropdown } from '../UI';
import { UploadRequiredDocument } from './UploadRequiredDocument';
import { useStrategyModals } from './useStrategyModals';
import { useStrategyActions } from './useStrategyActions';
import { getLabelStatus } from '../../Utilis/getLabelStatus';

type StrategyDocumetsProps = {
  requiredDocuments: any[];
  title: string;
  description: string;
  handleFileUpload?: (file: File, requiredDocumentId: string) => void;
  handleDocumentDelete?: (documentId: string) => void;
  handleRequiredDocumentDelete?: (requiredDocumentId: string) => void;
  handleRequiredDocumentUpload?: (requiredDocumentName: string) => void;
};

const StrategyDocuments = ({
  requiredDocuments,
  title,
  description,
  handleFileUpload,
  handleDocumentDelete,
  handleRequiredDocumentDelete,
  handleRequiredDocumentUpload,
}: StrategyDocumetsProps) => {
  const { openRequiredDocumentDeleteModal, openUploadModal } =
    useStrategyModals({
      handleRequiredDocumentDelete,
      handleRequiredDocumentUpload,
    });

  const { documentActions, handleDownload, uploadDocumentActions } =
    useStrategyActions({
      openRequiredDocumentDeleteModal,
      handleDocumentDelete,
    });

  const userState = useAppSelector(state => state.user);

  const isAdmin = userState.user?.role !== 'user';
  const isClientView = userState.user?.role !== 'user' && !!userState.client;

  const DocumentIcon = ({ children }: PropsWithChildren) => {
    return (
      <Box
        sx={{
          backgroundColor: '#E5FAFF',
          padding: '8px',
          borderRadius: '8px',
        }}
      >
        {children}
      </Box>
    );
  };

  const BottomComponent = isAdmin && !isClientView && (
    <Box
      sx={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 0',
      }}
      onClick={openUploadModal}
    >
      <Plus
        style={{
          width: '20px',
          height: '20px',
          color: '#A6A6A6',
        }}
      />
      <Typography style={{ color: '#A6A6A6' }}>Require a document</Typography>
    </Box>
  );

  const EmptyListComponent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        margin: '24px 0',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '48px',
            height: '48px',
            backgroundColor: '#E5FAFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
          }}
        >
          <FileAttachmentIcon />
        </Box>
        <Typography>No documents in this section</Typography>
      </Box>
      {isAdmin && !isClientView && (
        <Button
          variant='contained'
          sx={{ textTransform: 'none', fontWeight: '600' }}
          onClick={openUploadModal}
        >
          <Plus
            style={{
              width: '20px',
              height: '20px',
              color: 'white',
              marginRight: '8px',
            }}
          />
          Require a document
        </Button>
      )}
    </Box>
  );

  const label = useMemo(() => {
    const allItems = requiredDocuments.length;
    const completedItems = requiredDocuments?.filter(
      (req: any) => !!req?.document
    ).length;
    const message = {
      completed: 'All uploaded',
      empty: 'No documents',
    };

    return getLabelStatus(allItems, completedItems, message);
  }, [requiredDocuments]);

  return (
    <Dropdown
      Header={
        <ContentCard
          title={`${title} (${requiredDocuments.length})`}
          description={description}
          label={label.text}
          labelProps={{
            color: label.color,
            backgroundColor: label.backgroundColor,
          }}
        />
      }
      items={requiredDocuments}
      renderItem={(requiredDoc: any) =>
        !!requiredDoc?.document ? (
          <Document
            key={requiredDoc.document.id}
            icon={
              <DocumentIcon>
                <FileAttachmentIcon />
              </DocumentIcon>
            }
            title={requiredDoc.name}
            uploadedAt={requiredDoc?.createdAt}
            actions={documentActions(requiredDoc)}
            onDownload={() => handleDownload!(requiredDoc.document)}
            download
            square
          />
        ) : (
          <UploadDocument
            key={requiredDoc.id}
            icon={
              <DocumentIcon>
                <UploadCloud />
              </DocumentIcon>
            }
            title={requiredDoc?.name}
            onChange={file => handleFileUpload!(file!, requiredDoc.id)}
            actions={uploadDocumentActions(requiredDoc)}
            disabled={isAdmin}
            square
          />
        )
      }
      EmptyListComponent={EmptyListComponent}
      BottomComponent={BottomComponent}
    />
  );
};

export default StrategyDocuments;
